import { all, takeLatest, call, put, select } from 'redux-saga/effects';
import { ActionType } from 'deox';

import { navigate } from '@reach/router';
import { executeAfterToast } from '@/utils';
import { ArticleInstance } from '@/services/service-directory/article';
import {
  fetchArticlesActions,
  fetchArticleBySlugActions,
  loadMoreArticlesActions,
  createArticleActions,
  editArticleActions,
  deleteArticleActions,
  fetchArticleViewOnlyActions,
  fetchArticleDashboardActions,
  fetchArticlesDashboardActions,
} from '@/redux/actions/service-directory/article';
import { RootState } from '@/redux/reducers';
import { PAGE_SIZE_DEFAULT } from '@/pages/MyArticles/MyArticles.data';
import env, { SiteId } from '@/env';

export function* fetchArticlesSaga(action: ActionType<typeof fetchArticlesActions.request>) {
  const { isGlobal, pageSize, pageNumber, keyword, organisationId, tag, recaptchaToken } = action.payload;
  try {
    const { data, paging } = yield call(
      ArticleInstance.fetchArticles,
      isGlobal,
      pageSize,
      pageNumber,
      keyword,
      organisationId,
      tag,
      recaptchaToken
    );
    yield put(fetchArticlesActions.success(data, paging));
  } catch (err) {
    yield put(fetchArticlesActions.failure(err));
  }
}

export function* fetchArticlesDashboardSaga(action: ActionType<typeof fetchArticlesDashboardActions.request>) {
  const { isGlobal, pageSize, pageNumber, keyword, organisationId } = action.payload;
  try {
    const { data, paging } = yield call(
      ArticleInstance.fetchArticles,
      isGlobal,
      pageSize,
      pageNumber,
      keyword,
      organisationId
    );
    yield put(fetchArticlesDashboardActions.success(data, paging));
  } catch (err) {
    yield put(fetchArticlesDashboardActions.failure(err));
  }
}

export function* loadMoreArticlesSaga(action: ActionType<typeof loadMoreArticlesActions.request>) {
  const { isGlobal, pageSize, pageNumber, keyword } = action.payload;
  try {
    const { data, paging } = yield call(ArticleInstance.fetchArticles, isGlobal, pageSize, pageNumber, keyword);
    yield put(loadMoreArticlesActions.success({ data, paging }));
  } catch (err) {
    yield put(loadMoreArticlesActions.failure(err));
  }
}

export function* fetchArticleViewOnlySaga(action: ActionType<typeof fetchArticleViewOnlyActions.request>) {
  const { slug, recaptchaToken } = action.payload;
  try {
    const article = yield call(ArticleInstance.fetchArticleBySlug, slug, recaptchaToken);
    yield put(fetchArticleViewOnlyActions.success(article));
  } catch (err) {
    yield put(fetchArticleViewOnlyActions.failure(err));
  }
}

export function* fetchArticleDashboardSaga(action: ActionType<typeof fetchArticleDashboardActions.request>) {
  const { slug } = action.payload;
  try {
    const article = yield call(ArticleInstance.fetchArticleBySlug, slug);
    yield put(fetchArticleDashboardActions.success(article));
  } catch (err) {
    yield put(fetchArticleDashboardActions.failure(err));
  }
}

export function* fetchArticleBySlugSaga(action: ActionType<typeof fetchArticleBySlugActions.request>) {
  const { slug } = action.payload;
  try {
    const article = yield call(ArticleInstance.fetchArticleBySlug, slug);
    yield put(fetchArticleBySlugActions.success(article));
  } catch (err) {
    yield put(fetchArticleBySlugActions.failure(err));
  }
}

export function* createArticleSaga(action: ActionType<typeof createArticleActions.request>) {
  try {
    const { article, isGlobal } = action.payload;
    const organisationId = yield select((state: RootState) => state.serviceDirectoryEntity.entityDashboard?.id);
    yield call(ArticleInstance.createArticle, article, organisationId);
    yield put(createArticleActions.success());

    // refetch articles
    yield put(fetchArticlesActions.request(isGlobal, 0, PAGE_SIZE_DEFAULT, '', organisationId));

    if (env.siteId === SiteId.Directory) {
      const currentDashboardProduct = window.location.pathname.split('/')[1];
      executeAfterToast(() => navigate(`/${currentDashboardProduct}/dashboard/my-articles`));
    } else {
      executeAfterToast(() => navigate(`/dashboard/my-articles`));
    }
  } catch (err) {
    yield put(createArticleActions.failure(err));
  }
}

export function* editArticleSaga(action: ActionType<typeof editArticleActions.request>) {
  try {
    const { id, article, isGlobal } = action.payload;
    const organisationId = yield select((state: RootState) => state.serviceDirectoryEntity.entityDashboard?.id);

    yield call(ArticleInstance.updateArticle, id, article, organisationId);
    yield put(editArticleActions.success());

    // refetch articles
    yield put(fetchArticlesActions.request(isGlobal, 0, PAGE_SIZE_DEFAULT, '', organisationId));

    if (env.siteId === SiteId.Directory) {
      const currentDashboardProduct = window.location.pathname.split('/')[1];
      executeAfterToast(() => navigate(`/${currentDashboardProduct}/dashboard/my-articles`));
    } else {
      executeAfterToast(() => navigate(`/dashboard/my-articles`));
    }
  } catch (err) {
    yield put(editArticleActions.failure(err));
  }
}

export function* deleteArticleSaga(action: ActionType<typeof deleteArticleActions.request>) {
  try {
    const { id, isGlobal } = action.payload;
    const organisationId = yield select((state: RootState) => state.serviceDirectoryEntity.entityDashboard?.id);

    yield call(ArticleInstance.deleteArticle, id, organisationId);
    yield put(deleteArticleActions.success());

    // refetch articles
    yield put(fetchArticlesActions.request(isGlobal, 0, PAGE_SIZE_DEFAULT, '', organisationId));

    if (env.siteId === SiteId.Directory) {
      const currentDashboardProduct = window.location.pathname.split('/')[1];
      executeAfterToast(() => navigate(`/${currentDashboardProduct}/dashboard/my-articles`));
    } else {
      executeAfterToast(() => navigate(`/dashboard/my-articles`));
    }
  } catch (err) {
    yield put(deleteArticleActions.failure(err));
  }
}

export default function* root() {
  yield all([takeLatest(fetchArticlesActions.request.type, fetchArticlesSaga)]);
  yield all([takeLatest(fetchArticlesDashboardActions.request.type, fetchArticlesDashboardSaga)]);
  yield all([takeLatest(loadMoreArticlesActions.request.type, loadMoreArticlesSaga)]);
  yield all([takeLatest(fetchArticleBySlugActions.request.type, fetchArticleBySlugSaga)]);
  yield all([takeLatest(fetchArticleViewOnlyActions.request.type, fetchArticleViewOnlySaga)]);
  yield all([takeLatest(fetchArticleDashboardActions.request.type, fetchArticleDashboardSaga)]);
  yield all([takeLatest(createArticleActions.request.type, createArticleSaga)]);
  yield all([takeLatest(editArticleActions.request.type, editArticleSaga)]);
  yield all([takeLatest(deleteArticleActions.request.type, deleteArticleSaga)]);
}
